import React, { useState, useEffect, useRef } from 'react';
import { Flipper, Flipped } from 'react-flip-toolkit';
import VisibilitySensor from 'react-visibility-sensor';
import cn from 'classnames';
import ReactPlayer from 'react-player';

import styles from './gallery.module.css';

const AnimatedTabs = ({
  tabItems,
  duration,
  batch,
  theme,
  browserTitle,
  aspectRatio,
  gradientColor = [0, 0, 0],
  firstSegmentName,
  secondSegmentName
}) => {
  const [activeTab, setActiveTab] = useState(tabItems[0]);
  const [start, setStart] = useState(false);
  const timer = useRef();
  const current = activeTab;
  const currentIndex = tabItems.findIndex(tabItem => tabItem === current);
  const playerRef = useRef(null);
  const [overflow, setOverflow] = useState({ overflowLeft: false, overflowRight: false });
  const ref = useRef(null);
  // Calculate the columns based on the data we receive
  const firstBatchColumns = tabItems.filter(item => item.onFirstBatch).length || 5;
  const secondBatchColumns = tabItems.filter(item => item.onSecondBatch).length || 3;
  const batchesColumns =
    tabItems.filter(item => item.onFirstBatch).length +
      tabItems.filter(item => item.onSecondBatch).length || 8;

  useEffect(() => {
    if (!ref.current) return;

    const tabsRef = ref?.current;

    const handleResizeOrScroll = () => {
      const { scrollWidth, offsetWidth, scrollLeft } = tabsRef;
      const spaceLeft = offsetWidth + scrollLeft;

      if (scrollWidth === offsetWidth) {
        setOverflow({ overflowLeft: false, overflowRight: false });
      } else if (spaceLeft === scrollWidth) {
        setOverflow({ overflowLeft: true, overflowRight: false });
      } else if (spaceLeft === offsetWidth) {
        setOverflow({ overflowLeft: false, overflowRight: true });
      } else {
        setOverflow({ overflowLeft: true, overflowRight: true });
      }
    };

    const resizeObserver = new ResizeObserver(handleResizeOrScroll);

    tabsRef.addEventListener('scroll', handleResizeOrScroll);
    resizeObserver.observe(tabsRef);

    handleResizeOrScroll();

    return () => {
      tabsRef?.removeEventListener('scroll', handleResizeOrScroll);
      resizeObserver.disconnect();
    };
  }, [ref]);

  useEffect(() => {
    timer.current = setInterval(() => {
      if (start && tabItems.length - 1 > currentIndex && !activeTab.video) {
        setActiveTab(tabItems[currentIndex + 1]);
      }
    }, duration);
    return () => {
      clearInterval(timer.current);
    };
  }, [activeTab, start]);

  const onNextVideo = () => {
    if (tabItems.length - 1 > currentIndex) {
      setActiveTab(tabItems[currentIndex + 1]);
    } else {
      setActiveTab(tabItems[0]);
    }
  };

  const onVisibilityChange = isVisible => {
    if (isVisible) {
      setStart(true);
    }
  };

  return (
    <>
      <VisibilitySensor onChange={onVisibilityChange} scrollCheck>
        <div className={cn(styles.tabGrid, 'grid gap-10')}>
          <div className="hidden lg:block" />
          <div className="relative">
            <div
              style={{
                background: `linear-gradient(to left, rgba(0, 0, 0, 0), rgb(${gradientColor.toString()}))`
              }}
              className={cn(
                'pointer-events-none absolute left-0 top-0 bottom-[1px] z-20 w-1/6 opacity-0 transition-opacity duration-300',
                overflow.overflowLeft && 'opacity-100'
              )}
            />
            <div
              style={{
                background: `linear-gradient(to right, rgba(0, 0, 0, 0), rgb(${gradientColor.toString()})`
              }}
              className={cn(
                'pointer-events-none absolute right-0 top-0 bottom-[1px] z-20 w-1/6 opacity-0 transition-opacity duration-300',
                overflow.overflowRight && 'opacity-100'
              )}
            />
            <div ref={ref} className={cn('relative overflow-auto', batch ? 'pb-28' : 'pb-9')}>
              <div className="absolute md:min-w-full">
                {batch && (
                  <div className={cn('mb-6 grid pt-10', `grid-cols-${batchesColumns}`)}>
                    <div
                      className={cn(
                        'h-[14px] border-neutral-600 text-base text-scale-lightskyblue lg:rounded-t-lg lg:border-t lg:border-l lg:border-r lg:text-center',
                        `col-span-${firstBatchColumns}`
                      )}
                      style={{ width: '96%' }}
                    >
                      <span className="block transform -translate-y-4 lg:-translate-y-10">
                        {firstSegmentName || 'Calibration Batch'}
                      </span>
                    </div>
                    <div
                      className={cn(
                        'h-[14px] w-full border-neutral-600 text-base text-scale-lightskyblue lg:rounded-t-lg lg:border-t lg:border-l lg:border-r lg:text-center',
                        `col-span-${secondBatchColumns}`
                      )}
                    >
                      <span className="block transform -translate-y-4 lg:-translate-y-10">
                        {secondSegmentName || 'Production Batch'}
                      </span>
                    </div>
                  </div>
                )}
                <Flipper flipKey={activeTab}>
                  <div className={cn('flex', batch ? 'justify-between' : 'md:gap-x-6 xl:gap-x-12')}>
                    {tabItems?.map(t => (
                      <button
                        key={t.label}
                        onClick={() => setActiveTab(t)}
                        type="button"
                        className="relative"
                      >
                        {activeTab === t ? (
                          <Flipped flipId="tabBg">
                            <div
                              className="absolute inset-0 tabBg rounded-3xl bg-scale-mauve"
                              style={{ willChange: 'transform' }}
                            />
                          </Flipped>
                        ) : null}
                        <div
                          className={cn(
                            activeTab === t ? 'text-white' : textTheme[theme],
                            'relative z-10 whitespace-nowrap py-1.5 px-4 text-sm transition-colors duration-200 ease-in-out'
                          )}
                        >
                          {t.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </Flipper>
              </div>
            </div>
          </div>
          <div className={cn('hidden lg:block')}>
            <h1
              className={cn(
                styles.quote,
                textTheme[theme],
                'relative hidden py-2 pl-4 text-base leading-loose lg:block'
              )}
            >
              {activeTab.text}
            </h1>
            {activeTab.node}
          </div>
          <div className="overflow-hidden rounded-lg">
            <TopBar theme={theme} browserTitle={browserTitle} />
            <div className="relative" style={{ paddingTop: aspectRatio }}>
              {activeTab.video && (
                <ReactPlayer
                  ref={playerRef}
                  className="absolute top-0 left-0"
                  url={activeTab.video.src}
                  playing={start}
                  muted
                  controls
                  pip={false}
                  width="100%"
                  height="100%"
                  onEnded={onNextVideo}
                />
              )}
              {activeTab.img && (
                <div className="absolute top-0 left-0">
                  <img src={activeTab.img.src} alt={activeTab.img.alt} />
                </div>
              )}
            </div>
          </div>
          <h1
            className={cn(
              styles.horizontalQuote,
              textTheme[theme],
              'relative pt-8 text-base leading-loose lg:hidden'
            )}
          >
            {activeTab.text}
          </h1>
        </div>
      </VisibilitySensor>
    </>
  );
};

export const TopBar = ({ theme, browserTitle }) => (
  <div className={cn(browserTheme[theme], 'flex items-center justify-between border-b py-2 px-4 ')}>
    <div className="flex items-center">
      <span className={cn(browserButtonTheme[theme], 'mr-2 block h-2 w-2 rounded-full')} />
      <span className={cn(browserButtonTheme[theme], 'mr-2 block h-2 w-2 rounded-full')} />
      <span className={cn(browserButtonTheme[theme], 'block h-2 w-2 rounded-full')} />
    </div>
    <div
      className={cn(
        browserButtonTheme[theme],
        'h-6 w-1/2 rounded-lg text-center text-xs leading-6 text-neutral-800'
      )}
    >
      {browserTitle}
    </div>
    <div className="flex items-center">
      <div className={cn(browserButtonTheme[theme], 'mr-2 h-6 w-6 rounded-lg')} />
      <div className={cn(browserButtonTheme[theme], 'h-6 w-6 rounded-lg')} />
    </div>
  </div>
);

const browserTheme = {
  dark: 'bg-white border-neutral-100',
  light: 'bg-neutral-900 border-neutral-800'
};

const browserButtonTheme = {
  dark: 'bg-neutral-200',
  light: 'bg-neutral-300'
};

const textTheme = {
  dark: 'text-white',
  light: 'text-black'
};

AnimatedTabs.defaultProps = {
  theme: 'dark',
  browserTitle: 'Scale Rapid',
  tabItems: [],
  duration: 5000,
  batch: false,
  aspectRatio: '61.56%'
};

export default AnimatedTabs;
